// cncQuote.ts — CNC 견적 세부원가 순수 계산: 재료비 → 가공비 → 제품원가.
//
// 백엔드 `apps/api/api/core/cost/cnc_quote.py` 와 동일한 수식/결과를 유지해야 한다
// (CLAUDE.md 패리티 — 한쪽 수정 시 양쪽 + 테스트 동시 수정).
//
// 파라미터 주도형 — 단가/임율/기계경비는 CNC 마스터(DB row)에서 호출자가 채워서 넘긴다.
// 상수는 단일 출처를 재사용한다 (재정의 금지):
//   여유율 기본/3600  → @/lib/cost/labor (DEFAULT_ALLOWANCE, SECONDS_PER_HOUR)
//   일반/재료관리비율 → @/lib/cost/admin (GENERAL_ADMIN_RATE_INHOUSE, MATERIAL_ADMIN_RATE)
//
// 단위: 중량은 그램(g), 단가는 ₩/g, 원가는 ₩/ea.
//
// 수식:
//   원소재비   = 투입중량(g) × 원소재단가(₩/g)
//   스크랩비   = 스크랩중량(g) × 스크랩단가 × 회수율 (스크랩중량 미입력 시 투입−제품 자동 — 호출자가 채움)
//               회수율 = 0~1 분수, 미지정 시 1(100% — 회수율 도입 전 저장 견적 호환, 내용추가18)
//   원재료비   = 원소재비 − 스크랩비
//   표면처리비 = Σ 표면처리 라인 (단위 kg → (제품중량/1000)×단가 / g → 제품중량×단가 / ea → 단가)
//   열처리비   = Σ 열처리 라인 (동일 규칙)
//   기타비     = Σ 기타 라인 (동일 규칙)
//   재료비     = 원재료비 + 표면처리비 + 열처리비 + 기타비
//   노무비     = 인원 × C/T × 임율 / 3600 × (1 + 여유율)
//   경비       = C/T × 기계경비 / 3600 × (1 + 여유율)
//   가공비     = 노무비 + 경비
//   제조원가   = 재료비 + 가공비
//   일반관리비 = 제조원가 × 일반관리비율 (기본 0.17)
//   재료관리비 = 재료비 × 재료관리비율 (기본 0.02)
//   제품원가   = 제조원가 + 일반관리비 + 이윤 + 재료관리비 + 금형비 + 기타비용

import { GENERAL_ADMIN_RATE_INHOUSE, MATERIAL_ADMIN_RATE } from "@/lib/cost/admin";
import { DEFAULT_ALLOWANCE, SECONDS_PER_HOUR, allowanceForProcess } from "@/lib/cost/labor";

// ----- 재료비 -----

// 스크랩 회수율 기본값 (%) — 원재료 마스터 재질별 값의 기본(내용추가18). 계산 입력은 0~1 분수.
// 백엔드 api/core/cost/cnc_quote.py DEFAULT_SCRAP_RECOVERY_RATE 와 동일.
export const DEFAULT_SCRAP_RECOVERY_RATE = 90;

// 표면처리/열처리/기타 단가 기준 — kg(KG당) / ea(개당) / g(중량당, legacy).
// "g" 는 기존 저장 견적 호환을 위해 유지한다(견적 불변성 — 동일 입력은 동일 결과).
export type TreatmentUnit = "g" | "ea" | "kg";

// 표면처리/열처리/기타 한 항목(라인)의 계산 입력.
export interface CncTreatmentInput {
  unit: TreatmentUnit; // kg → (제품중량/1000)×단가, g → 제품중량×단가, ea → 단가
  unitPrice: number; // ₩/kg, ₩/g 또는 ₩/ea
}

export interface CncMaterialInput {
  inputWeightG: number; // 투입중량 (g)
  productWeightG: number; // 제품중량 (g)
  rawUnitPrice: number; // 원소재 단가 (₩/g)
  scrapUnitPrice: number; // 스크랩 단가 (₩/g)
  scrapWeightG: number; // 스크랩중량 (g) — 호출자가 채움(미입력 시 투입−제품 fallback 적용 후)
  scrapRecoveryRate?: number; // 스크랩 회수율 (0~1 분수) — 미지정 시 1(100%, 기존 견적 불변)
  surfaceLines: CncTreatmentInput[]; // 표면처리 라인
  heatLines: CncTreatmentInput[]; // 열처리 라인
  etcLines: CncTreatmentInput[]; // 기타비 라인
}

export interface CncMaterialResult {
  rawMaterialCost: number; // 원소재비 = 투입중량 × 원소재단가
  scrapCredit: number; // 스크랩비 = 스크랩중량 × 스크랩단가 × 회수율
  netMaterialCost: number; // 원재료비 = 원소재비 − 스크랩비
  surfaceCost: number; // 표면처리비 = Σ 표면처리 라인
  heatCost: number; // 열처리비 = Σ 열처리 라인
  etcCost: number; // 기타비 = Σ 기타 라인
  materialCost: number; // 재료비 = 원재료비 + 표면처리비 + 열처리비 + 기타비
}

// 라인 1개의 비용(₩/ea): kg → (제품중량/1000)×단가(₩/kg), g → 제품중량(g)×단가(₩/g), ea → 단가.
// "g"/"ea" 경로는 기존 저장 견적 호환을 위해 변경하지 않는다(견적 불변성).
export function lineCost(line: CncTreatmentInput, productWeightG: number): number {
  if (line.unit === "kg") return (productWeightG / 1000) * line.unitPrice;
  if (line.unit === "g") return productWeightG * line.unitPrice;
  return line.unitPrice;
}

const sumLines = (lines: CncTreatmentInput[], productWeightG: number): number =>
  lines.reduce((acc, l) => acc + lineCost(l, productWeightG), 0);

// 재료비(1ea)를 산출한다.
export function cncMaterialCost(input: CncMaterialInput): CncMaterialResult {
  const rawMaterialCost = input.inputWeightG * input.rawUnitPrice;
  const scrapCredit = input.scrapWeightG * input.scrapUnitPrice * (input.scrapRecoveryRate ?? 1);
  const netMaterialCost = rawMaterialCost - scrapCredit;
  const surfaceCost = sumLines(input.surfaceLines, input.productWeightG);
  const heatCost = sumLines(input.heatLines, input.productWeightG);
  const etcCost = sumLines(input.etcLines, input.productWeightG);
  const materialCost = netMaterialCost + surfaceCost + heatCost + etcCost;
  return { rawMaterialCost, scrapCredit, netMaterialCost, surfaceCost, heatCost, etcCost, materialCost };
}

// ----- 가공비 (공정별 노무비 + 경비) -----

export interface CncProcessInput {
  processName: string; // 공정명 (표시용 + 여유율 미적용 공정 식별 키 — 최종검사/포장은 여유율 0)
  ctSec: number; // C/T 작업시간(초)
  headcount: number; // 인원
  hourlyLaborRate: number; // 임율 (₩/hr) — CNC 마스터 임율 row
  machineHourlyRate: number; // 기계경비 (₩/hr) — machineExpense(...).hourlyRate
  allowance?: number; // 여유율, 기본 DEFAULT_ALLOWANCE (최종검사/포장은 공정명으로 0 강제)
}

export interface CncProcessResult {
  processName: string;
  laborCost: number; // 노무비 = 인원 × C/T × 임율/3600 × (1+여유율)
  expenseCost: number; // 경비 = C/T × 기계경비/3600 × (1+여유율)
  processingCost: number; // 가공비 = 노무비 + 경비
}

export interface CncProcessingResult {
  perProcess: CncProcessResult[];
  laborTotal: number; // 노무비계
  expenseTotal: number; // 경비계
  processingTotal: number; // 가공비계
}

// 공정 리스트의 가공비(1ea)를 산출한다. 여유율 ×(1+S)는 노무비/경비에만 적용.
export function cncProcessingCost(processes: CncProcessInput[]): CncProcessingResult {
  const perProcess: CncProcessResult[] = processes.map((p) => {
    // 최종검사/포장은 여유율 0 강제(공정명 식별), 그 외는 공정별 입력/기본값. (#79)
    const allowance = allowanceForProcess(p.processName, p.allowance ?? DEFAULT_ALLOWANCE);
    const laborCost =
      ((p.headcount * p.ctSec * p.hourlyLaborRate) / SECONDS_PER_HOUR) * (1 + allowance);
    const expenseCost = ((p.ctSec * p.machineHourlyRate) / SECONDS_PER_HOUR) * (1 + allowance);
    return {
      processName: p.processName,
      laborCost,
      expenseCost,
      processingCost: laborCost + expenseCost,
    };
  });
  const laborTotal = perProcess.reduce((acc, r) => acc + r.laborCost, 0);
  const expenseTotal = perProcess.reduce((acc, r) => acc + r.expenseCost, 0);
  return { perProcess, laborTotal, expenseTotal, processingTotal: laborTotal + expenseTotal };
}

// ----- 금형비 안분 -----

// 금형비(1ea) = 양산수량 > 0 이면 총금형비 / 양산수량, 아니면 총금형비 그대로.
export function amortizedMoldCost(totalMoldCost: number, productionQty: number): number {
  return productionQty > 0 ? totalMoldCost / productionQty : totalMoldCost;
}

// ----- 견적 합산 -----

export interface CncQuoteInput {
  material: CncMaterialInput;
  processes: CncProcessInput[];
  profit: number; // 이윤 (₩/ea) — profitRate 미지정 시 사용
  // 마진율 모드(판매가 기준 영업이익률, 0~1 분수): 제품원가 = 내부원가 ÷ (1 − profitRate),
  // 이윤 = 제품원가 − 내부원가. 지정 시 profit 무시. profitRate ≥ 1(100%) 은 정의 불가 → 이윤 0 가드.
  profitRate?: number;
  moldCost: number; // 금형비 (₩/ea — 안분 후)
  miscCost: number; // 기타비용 (₩/ea)
  generalAdminRate?: number; // 일반관리비율, 기본 GENERAL_ADMIN_RATE_INHOUSE
  materialAdminRate?: number; // 재료관리비율, 기본 MATERIAL_ADMIN_RATE
}

export interface CncQuoteResult {
  material: CncMaterialResult;
  processing: CncProcessingResult;
  manufacturingCost: number; // 제조원가 = 재료비 + 가공비
  generalAdmin: number; // 일반관리비 = 제조원가 × rate
  materialAdmin: number; // 재료관리비 = 재료비 × rate
  profit: number; // 이윤 (금액 모드=입력 보존 / 마진율 모드=내부원가×율 산출)
  moldCost: number; // 금형비 (입력 보존)
  miscCost: number; // 기타비용 (입력 보존)
  productCost: number; // 제품원가 = 제조원가 + 일반관리비 + 이윤 + 재료관리비 + 금형비 + 기타비용
}

// CNC 견적 세부원가 전체를 산출한다.
export function cncQuote(input: CncQuoteInput): CncQuoteResult {
  const material = cncMaterialCost(input.material);
  const processing = cncProcessingCost(input.processes);
  const generalAdminRate = input.generalAdminRate ?? GENERAL_ADMIN_RATE_INHOUSE;
  const materialAdminRate = input.materialAdminRate ?? MATERIAL_ADMIN_RATE;

  const manufacturingCost = material.materialCost + processing.processingTotal;
  const generalAdmin = manufacturingCost * generalAdminRate;
  const materialAdmin = material.materialCost * materialAdminRate;
  // 내부원가(이윤 제외) = 제조원가 + 일반관리비 + 재료관리비 + 금형비 + 기타비용.
  const internalBeforeProfit =
    manufacturingCost + generalAdmin + materialAdmin + input.moldCost + input.miscCost;
  // 마진율 모드(판매가 기준): 제품원가 = 내부원가 ÷ (1 − 마진율), 이윤 = 제품원가 − 내부원가.
  // 미지정이면 입력 이윤 금액을 가산. 마진 ≥ 100% 는 정의 불가 → 이윤 0.
  let profit: number;
  let productCost: number;
  if (input.profitRate != null) {
    const divisor = 1 - input.profitRate;
    productCost = divisor > 0 ? internalBeforeProfit / divisor : internalBeforeProfit;
    profit = productCost - internalBeforeProfit;
  } else {
    profit = input.profit;
    productCost = internalBeforeProfit + profit;
  }

  return {
    material,
    processing,
    manufacturingCost,
    generalAdmin,
    materialAdmin,
    profit,
    moldCost: input.moldCost,
    miscCost: input.miscCost,
    productCost,
  };
}
